import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, ToastProvider } from "@ozpanel/ui";
import { App } from "./App";
import { MAILBOX_PAGES } from "./pages";
import { applyAppIdentity } from "./appIdentity";
import "./installPrompt";
import "./index.css";

// ONE address scheme for the whole app: every page is <base>/<page> (base = /ozpanel/ in a
// release), so Back moves between pages and a page can be bookmarked or linked.
//
// The same bundle is ALSO served by the webmail hub at "/" and "/app", and links in the wild still
// carry "?open=<page>" (a new-mail push opens "/?open=mail"; the quarantine digest links
// "?open=spam") and "?client=<name>" (the webmail hand-off). Those are translated here, ONCE,
// before the router starts, so the router only ever sees one form and an old link still lands on
// the page it always meant.
const BASE = import.meta.env.BASE_URL.replace(/\/+$/, "");

function canonicalise(): void {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  let path = pathname;
  const underBase = BASE === "" || path === BASE || path.startsWith(BASE + "/");
  if (!underBase) {
    // "/app" is the framed-webmail entry; anything else outside the base is the hub's root.
    path = BASE + "/" + (/\/app\/?$/.test(path) ? "mail" : "");
  }
  const open = params.get("open");
  if (open !== null) {
    params.delete("open");
    const page = MAILBOX_PAGES.find((p) => p.key === open || (open === "dashboard" && p.key === "overview"));
    if (page) path = BASE + "/" + page.path;
  }
  if (params.get("client") && !/\/mail\/?$/.test(path)) path = BASE + "/mail";
  const q = params.toString();
  const next = path + (q ? "?" + q : "") + hash;
  if (next !== pathname + search + hash) window.history.replaceState(null, "", next);
}

canonicalise();
applyAppIdentity(BASE);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Auth failures (401) should not retry silently — a stale session should surface as a
      // "signed out" state, not a spinner that quietly retries forever.
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | undefined)?.status;
        if (status === 401) return false;
        return failureCount < 2;
      },
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("#root element missing from index.html");
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Outside App so a toast survives a page change — and because without it useToast throws
          and takes the whole console down. */}
      <ThemeProvider>
        <ToastProvider>
          <App />
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
